import { format } from "date-fns";
import type {
  FullPlanId,
  Plan,
  SearchResultPlan,
  IdParts,
  NewRelicPageActionValuesMap,
  NewRelicPageActionInterface,
  IdPartsWithYear,
} from "../@types/types";
import type { PlanLongId } from "../@types";
import URI from "urijs";

export function getFullPlanId(plan: Plan | SearchResultPlan): FullPlanId {
  return {
    contract_id: plan.contract_id,
    contract_year: plan.contract_year,
    plan_id: plan.plan_id,
    segment_id: plan.segment_id,
  };
}

export function makePlanDisplayId(plan: IdParts): string {
  const { contract_id, plan_id, segment_id } = plan;
  return `${contract_id}-${plan_id}-${segment_id}`;
}

/**
 * Combines a year part and 3 ID parts
 *
 * NOTE: Due to legacy typing, one of three optional properties defines the year part.
 * - `coverage_year`
 * - `contract_year`
 * - `year` (from `OecStatus`)
 *
 * Be sure one will be defined when calling `makePlanLongId`
 */
export function makePlanLongId(idParts: IdPartsWithYear): PlanLongId {
  const {
    contract_year,
    coverage_year,
    year,
    contract_id,
    plan_id,
    segment_id,
  } = idParts;
  return `${
    contract_year || coverage_year || year
  }-${contract_id}-${plan_id}-${segment_id}`;
}

/**
 * Allows easy conversion of a `PlanLongId` (e.g., from a URL) to an object of
 * its constituent parts.
 */
export function convertPlanLongIdToParts(longId: PlanLongId): IdParts {
  const partsMap = {
    0: "contract_year",
    1: "contract_id",
    2: "plan_id",
    3: "segment_id",
  } as const;
  const partsArray = longId.split("-");
  const parts = {} as IdParts;
  let i = 0;
  for (const part of partsArray) {
    parts[partsMap[i as 0 | 1 | 2 | 3]] = part;
    i++;
  }
  return parts;
}

export function makePlanUrlPartForApi(plan: IdParts): string {
  const { contract_year, contract_id, plan_id, segment_id } = plan;
  return `${contract_year}/${contract_id}/${plan_id}/${segment_id}`;
}

/**
 * Parses plan long id into Url parts for API consumption
 * @param longId the plan id. The plan parts are delimited by "-". Example: 2021-H5652-002-0
 * @returns the plan id as path components delimited by "/". Example: 2021/H5652/002/0
 */
export function parsePlanLongIdIntoUrlPartForApi(longId: PlanLongId): string {
  const parts = convertPlanLongIdToParts(longId);
  const apiURL = Object.values(parts).join("/");
  return apiURL;
}

export function addProtocolToUrl(url: string): string {
  if (url.indexOf("http") !== 0) {
    return `http://${url}`;
  }
  return url;
}

export function safeMatches(
  elem: HTMLElement & {
    msMatchesSelector?: ((selectors: string) => boolean) | undefined;
  },
  comparison: string
): boolean {
  if (elem.matches) {
    return elem.matches(comparison);
  } else if (elem.msMatchesSelector) {
    return elem.msMatchesSelector(comparison);
  } else {
    return false;
  }
}

export function stripParens(str: string): string {
  return str.replaceAll(/[()]/g, "");
}

export function isMobileWidth(): boolean {
  return window.innerWidth < 1024;
}

export function isWindowMobileOuterWidth(window: Window): boolean {
  return window.outerWidth < 1024;
}

export type IsBetweenValidator = (
  value: number,
  low: number,
  high: number
) => boolean;

export const inputIsBetween: IsBetweenValidator = (value, low, high) => {
  const tooLow = value < low;
  const tooHigh = value > high;
  return !tooLow && !tooHigh;
};

export function limitInputLengthCustomSelector(
  selector: string,
  limit: number
): void {
  const field = document.querySelector(selector) as HTMLInputElement;

  if (field) {
    const { value } = field;

    if (value.length > limit) {
      field.value = value.slice(0, Math.max(0, limit));
    }
  }
}

export const limitInputValueLength = ({
  inputVal,
  limit,
}: {
  inputVal: string;
  limit: number;
}): string =>
  inputVal.length > limit ? inputVal.slice(0, Math.max(0, limit)) : inputVal;

export function limitInputLength(
  fieldName: string | null,
  limit: number,
  inputIndex = 0
): void {
  const field = document.querySelectorAll(`.ds-c-field--${fieldName}`)[
    inputIndex
  ] as HTMLInputElement;

  if (field) {
    const { value } = field;

    if (value.length > limit) {
      field.value = value.slice(0, Math.max(0, limit));
    }
  }
}

export function limitDateInputs(inputIndex = 0): void {
  const fields: [string, number][] = [
    ["month", 2],
    ["day", 2],
    ["year", 4],
  ];
  for (const [fieldName, limit] of fields)
    limitInputLength(fieldName, limit, inputIndex);
}

export function containsLetters(value: string): boolean {
  const regEx = /\D+/;
  return regEx.test(value);
}

export function filterFalsyOut<T>(payload: (T | undefined | null)[]): T[] {
  return payload.reduce((acc, item) => {
    if (item) acc.push(item);
    return acc;
  }, [] as T[]);
}

const iNewRelicPageAction: NewRelicPageActionInterface<NewRelicPageActionValuesMap> =
  {
    addNewRelicPageAction: (name, attributes) => {
      window.newrelic?.addPageAction(name, attributes);
    },
  };

/**
 * Typed alias for global `window.newrelic.addPageAction`
 * @see newrelic.addPageAction
 * @see newrelic.SimpleType
 */
export const addNewRelicPageAction = iNewRelicPageAction.addNewRelicPageAction;

export function isValidEmail(email: string): boolean {
  return (
    email.match(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/) !== null
  );
}
/**
 * Given a set of query params, if one is an array, use the first in the array
 * @TODO - To fix typing
 * @see MCT-9170
 * Not any random query param name is defined, so cast the return value to an
 * object where the values could be either `string`s or `undefined`
 */
export function parseSearchParams(params: {
  [x: string]: string | (string | null)[] | null;
}): {
  [x: string]: string | undefined;
} {
  for (const [key, value] of Object.entries(params)) {
    params[key] = Array.isArray(value)
      ? (value[0] as string)
      : (value as string);
  }
  return params as {
    [x: string]: string;
  };
}

export function getSLSCode(search: string): string | undefined {
  const { code } = parseSearchParams(URI.parseQuery(search));
  return code;
}

// given a 24 hour time, format to a 12 hour time
export function formatTime(time: string): string {
  const d = new Date(0, 0, 0, 0);
  d.setHours(Number(time.slice(0, 2)));
  d.setMinutes(Number(time.slice(2, 4)));
  return format(d, "p");
}

/** Reverse sorts any array of (only) strings or numbers */
export const sortDescending = <T extends (string | number)[]>(arr: T): T => {
  let clone = [...arr]; // don't mutate the original array
  const isNumArray = clone.every(v => typeof v === "number");
  const isNumberAsStringArray =
    !isNumArray && clone.every(v => !isNaN(Number(v)));
  if (isNumArray || isNumberAsStringArray) {
    if (isNumberAsStringArray) {
      // convert to numbers to sort correctly using a numeric sort
      clone = clone.map(Number);
    }
    // Reverse sort with sort callback
    (clone as number[]).sort((a, b) => b - a);
  } else {
    // sort strings using standard `sort` and `reverse`
    clone.sort();
    clone.reverse();
  }
  // Return arrays to their original types
  if (isNumArray) {
    clone = clone.map(Number);
  }
  if (isNumberAsStringArray) {
    clone = clone.map(v => `${v}`);
  }
  return clone as T;
};

/**
 * Returns the first key of an object, which is either the largest if a number or
 * number-as-string, or the last of strings sorted alphabetically
 */
export const getFirstReverseSortedKey = (
  obj: Record<string | number, unknown>
): string => sortDescending(Object.keys(obj))[0].toString();
